import React from 'react';
import { Inbox, MessageCircle, Instagram, Facebook, Users } from 'lucide-react';
import type { DrawerChannel } from '@/lib/stores/whatsappDrawerStore';
import { tip } from '@/components/messages/ChatTooltip';

interface ChannelSelectorProps {
  value: DrawerChannel;
  onChange: (channel: DrawerChannel) => void;
  /** Conteggi per il badge (non letti o da rispondere) per canale. */
  pending?: Partial<Record<DrawerChannel, number>>;
  /** Canali da mostrare (default: tutti). "Tutte" c'e' sempre. */
  visible?: Partial<Record<DrawerChannel, boolean>>;
  /** Solo icone (drawer stretto): il nome del canale resta nel tooltip e per i lettori di schermo. */
  iconsOnly?: boolean;
}

const CHANNELS: Array<{ id: DrawerChannel; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'all', label: 'Tutte', icon: Inbox },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { id: 'instagram', label: 'Instagram', icon: Instagram },
  { id: 'messenger', label: 'Messenger', icon: Facebook },
  { id: 'team', label: 'Team', icon: Users },
];

/**
 * Selettore di canale in testa al drawer "Messaggi": Tutte, WhatsApp,
 * Instagram, Messenger, Team. Segmented control Notion-like, stessa famiglia
 * dei toggle "full/compact" dell'header.
 */
export function ChannelSelector({ value, onChange, pending, visible, iconsOnly = false }: ChannelSelectorProps) {
  return (
    <div
      role="tablist"
      aria-label="Canale"
      className="inline-flex items-center gap-0.5 p-0.5 rounded-md bg-[rgba(55,53,47,0.06)] dark:bg-white/10"
    >
      {CHANNELS.filter(({ id }) => id === 'all' || visible?.[id] !== false).map(({ id, label, icon: Icon }) => {
        const active = value === id;
        const count = pending?.[id] ?? 0;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(id)}
            aria-label={count > 0 ? `${label}, ${count} da leggere` : label}
            {...tip(label, 'bottom')}
            className={[
              'inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap',
              active
                ? 'bg-white dark:bg-[#2f2f2f] text-[#37352F] dark:text-white shadow-sm'
                : 'text-[#5F5E5B] dark:text-white/70 hover:text-[#37352F] dark:hover:text-white',
            ].join(' ')}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className={iconsOnly ? 'sr-only' : 'hidden sm:inline'}>{label}</span>
            {count > 0 && (
              <span
                className="min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-[#2383e2] text-white text-[10px] font-semibold"
                aria-hidden="true"
              >
                {count > 99 ? '99+' : count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
